import { ExternalLink } from 'lucide-react'
import { Button, Text } from '../../ui'
import { SettingsSection } from './SettingsSection'
import {
  formatPlanFeatureValue,
  formatPlanLabel,
  getMaxMembers,
  PLAN_FEATURES,
  PLAN_PRICING,
  type Plan,
} from '../../../lib/plan-limits'
import { getPublicKbUrl } from '../../../lib/public-kb-host'
import type { Workspace } from '../../../lib/api'
import { Link } from '@tanstack/react-router'

type PlanSectionProps = {
  workspace: Workspace
}

const salesEmail = 'hello@usesondersoft.com'

function getPlanCta(plan: string): { label: string; to?: string; href?: string } {
  if (plan === 'enterprise') {
    return { label: 'Contact sales', href: `mailto:${salesEmail}` }
  }
  if (plan === 'starter' || plan === 'growth' || plan === 'business') {
    return { label: plan === 'starter' ? 'Upgrade' : 'Manage billing', to: '/billing' }
  }
  return { label: PLAN_PRICING[plan as Plan]?.cta ?? 'Manage billing', to: '/billing' }
}

export function PlanSection({ workspace }: PlanSectionProps) {
  const maxMembers = getMaxMembers(workspace.plan)
  const memberLabel =
    maxMembers === null
      ? `${workspace.memberCount} members (unlimited)`
      : `${workspace.memberCount} of ${maxMembers} members`

  const pricing = PLAN_PRICING[workspace.plan as Plan]
  const cta = getPlanCta(workspace.plan)
  const publicUrl = getPublicKbUrl(workspace.slug)

  return (
    <SettingsSection
      id="plan"
      title="Plan"
      description="Your workspace plan and feature limits."
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="inline-flex rounded-full border border-foreground/10 bg-stone px-3 py-1 text-sm font-medium capitalize">
              {formatPlanLabel(workspace.plan)}
            </span>
            <Text variant="caption" className="mt-2 block text-foreground/45">
              {pricing?.usd ?? '—'}
              {pricing?.ngn ? ` · ${pricing.ngn} for Nigerian businesses` : ''}
            </Text>
            <Text variant="caption" className="mt-1 block text-foreground/45">
              {memberLabel}
            </Text>
            <Text variant="caption" className="mt-1 block text-foreground/45">
              Public help center:{' '}
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {publicUrl.replace(/^https?:\/\//, '')}
              </a>
            </Text>
          </div>
          {cta.to ? (
            <Link to={cta.to} search={{ workspaceId: workspace.id }}>
              <Button variant="outline" size="sm" leftIcon={<ExternalLink className="h-4 w-4" />}>
                {cta.label}
              </Button>
            </Link>
          ) : (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ExternalLink className="h-4 w-4" />}
              onClick={() => window.open(cta.href, '_blank', 'noopener,noreferrer')}
            >
              {cta.label}
            </Button>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-foreground/10">
          <table className="w-full min-w-[320px] text-left text-sm">
            <thead>
              <tr className="border-b border-foreground/10 bg-stone/40">
                <th className="px-4 py-2 font-medium text-foreground/60">Feature</th>
                <th className="px-4 py-2 font-medium text-foreground/60">Included</th>
              </tr>
            </thead>
            <tbody>
              {PLAN_FEATURES.map((feature) => {
                const included = feature[workspace.plan as keyof typeof feature]
                return (
                  <tr key={feature.label} className="border-b border-foreground/10 last:border-0">
                    <td className="px-4 py-2.5 text-foreground/80">{feature.label}</td>
                    <td className="px-4 py-2.5 text-foreground/60">
                      {formatPlanFeatureValue(included)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SettingsSection>
  )
}
